import { Api } from '@/utils/Api'
import setEntities from '@/utils/SetEntities'
import UrlStore from '@/utils/UrlStore'
import { normalize, schema } from 'normalizr'
import Vue from 'vue'
import { extractEmployees } from './employee'
import { extractRooms } from './room'
import { extractPrinters } from '@/store/modules/printer'
import { extractPcs } from '@/store/modules/pc'
import { extractDesks } from '@/store/modules/desk'

export const POINTS_LOAD = 'POINTS_LOAD'
export const POINT_LOAD = 'POINT_LOAD'
export const POINT_CREATE = 'POINT_CREATE'
export const POINT_UPDATE = 'POINT_UPDATE'
export const POINT_DESTROY = 'POINT_DESTROY'
export const POINT_STATE_UPDATE = 'POINT_STATE_UPDATE'

export const DEFAULT_POINT_LAT_LON = '(-0.00008,-0.00006)'

const pointsApi = new Api(UrlStore.POINTS)
const pointSchema = new schema.Entity('points')

export const POINT_ICONS = {
  EMPLOYEE: 2,
  ROOM: 4,
  PRINTER: 5,

  CLOSED: 6,
  AED: 7,
  EYEWASH: 8,
  FIRE: 9,
  FIRSTAID: 10,

  DESK: 11,
  DESK_BUSY: 12,

  PC: 13,

  AREA: 14
}

export const POINTS_MAIN_ORDERED = [
  POINT_ICONS.EMPLOYEE,
  POINT_ICONS.DESK,
  POINT_ICONS.ROOM,
  POINT_ICONS.PRINTER,
  POINT_ICONS.PC,
  POINT_ICONS.AREA
]
export const POINTS_MAIN_ORDERED_WITH_BUSY_DESK = [
  POINT_ICONS.EMPLOYEE,
  POINT_ICONS.DESK,
  POINT_ICONS.DESK_BUSY,
  POINT_ICONS.ROOM,
  POINT_ICONS.PRINTER,
  POINT_ICONS.PC,
  POINT_ICONS.AREA
]

export function getIsPointHasPayload(point) {
  const pointType = POINT_TYPE_MAP[point.icon_id] ?? 'point'
  return pointType === 'point' || pointType === 'area' || (point[pointType] !== null && point[pointType] !== undefined)
}

export function filterDesk(id, hasDesk) {
  if (hasDesk) return true
  return id !== POINT_ICONS.DESK && id !== POINT_ICONS.DESK_BUSY
}

export const POINT_TYPE_MAP = {
  [POINT_ICONS.EMPLOYEE]: 'employee',
  [POINT_ICONS.ROOM]: 'room',
  [POINT_ICONS.PRINTER]: 'printer',
  [POINT_ICONS.DESK]: 'desk',
  [POINT_ICONS.DESK_BUSY]: 'desk',
  [POINT_ICONS.PC]: 'pc',
  [POINT_ICONS.AREA]: 'area'
}

export default {
  namespaced: true,
  state: {
    pointsLoaded: false,
    pointsList: [],
    entities: {
      points: {}
    }
  },
  getters: {
    points(state) {
      return state.pointsList.map((pId) => state.entities.points[pId]).filter(getIsPointHasPayload)
    },
    getPoint: (state) => (pointId) => {
      return state.entities.points[pointId] || null
    }
  },
  mutations: {
    [POINTS_LOAD](state, payload) {
      const normData = normalize(payload.items, [pointSchema])
      state.pointsList = normData.result
      setEntities(state, normData)
    },
    [POINT_LOAD](state, payload) {
      const normData = normalize(payload, pointSchema)
      setEntities(state, normData)
    },
    [POINT_CREATE](state, point) {
      state.pointsList.push(point.id)
      Vue.set(state.entities.points, point.id, point)
    },
    [POINT_UPDATE](state, point) {
      if (point.id && state.entities.points[point.id]) {
        state.entities.points[point.id] = Object.assign({}, state.entities.points[point.id], point)
      }
    },
    [POINT_DESTROY](state, point) {
      let num = state.pointsList.findIndex((cId) => cId === point.id)
      if (num !== -1) {
        state.pointsList.splice(num, 1)
      }
      delete state.entities.points[point.id]
    },
    [POINT_STATE_UPDATE](state, data) {
      for (let name of Object.keys(data)) {
        state[name] = data[name]
      }
    }
  },
  actions: {
    pointsLoad({ commit, getters }, config = {}) {
      return pointsApi.getAll(config).then((r) => {
        if (r.data.items.length) {
          extractEmployees({ commit }, r.data.items)
          extractRooms({ commit }, r.data.items)
          extractPrinters({ commit }, r.data.items)
          extractPcs({ commit }, r.data.items)
          extractDesks({ commit }, r.data.items)
        }
        commit(POINTS_LOAD, r.data)
        commit(POINT_STATE_UPDATE, { pointsLoaded: true })
      })
    },
    // just return a points list from the server
    // use in points refresh
    pointsSearch({ commit, getters }, config = {}) {
      return pointsApi.getAll(config)
    },
    pointLoad({ commit }, config) {
      return pointsApi.get({ id: config.id, config }).then((r) => {
        if (r.data) {
          extractEmployees({ commit }, [r.data])
          extractRooms({ commit }, [r.data])
          extractPrinters({ commit }, [r.data])
          extractPcs({ commit }, [r.data])
          extractDesks({ commit }, [r.data])
        }
        commit(POINT_LOAD, r.data)
      })
    },
    pointSave({ commit, getters }, point) {
      if (!point.id) throw new Error('Point id is not present in `pointSave`!')
      commit(getters.getPoint(point.id) ? POINT_UPDATE : POINT_CREATE, point)
      // we update title only if it directly passed
      const pointUpdated = { ...getters.getPoint(point.id), title: point.title ?? undefined }
      return pointsApi.save(pointUpdated).then((r) => {
        extractEmployees({ commit }, [r.data])
        extractRooms({ commit }, [r.data])
        extractPrinters({ commit }, [r.data])
        extractPcs({ commit }, [r.data])

        if (r._isNew) {
          // commit(POINT_DESTROY, pointUpdated) // local
          commit(POINT_CREATE, r.data)
        } else {
          commit(POINT_UPDATE, r.data)
        }
        return r.data.id
      })
    },
    pointDestroy({ commit }, point) {
      return pointsApi.destroy({ id: point.id }).then(() => {
        commit(POINT_DESTROY, point)
      })
    }
  }
}
